import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent,
    DialogTitle, FormControl, FormControlLabel, IconButton, InputLabel, MenuItem, Paper, Select,
    Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
    ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SeasonBreadcrumb from '../../../components/admin/SeasonBreadcrumb';
import { useSeason } from '../../../context/SeasonContext';
import { useNotification } from '../../../context/NotificationContext';
import {
    SYNC_INTERVALS, WEEKDAY_ORDER, WEEKDAY_SHORT, fetchTaskRuns, fetchTasks, formatSchedule,
    runTaskNow, updateTaskConfig,
} from '../../../services/tasks';
import { formatMoment } from '../../../utils/formatDate';

const STATUS_CHIP = {
    success: { color: 'success', label: 'Gelukt' },
    error: { color: 'error', label: 'Mislukt' },
    skipped: { color: 'default', label: 'Overgeslagen' },
};

/** De config-velden van een taak, losgeknipt van de weergavevelden en diep gekopieerd. */
function taskConfig(task) {
    if (task.triggerType === 'matchWindow') {
        return { enabled: task.enabled, matchWindow: { ...task.matchWindow } };
    }
    return {
        enabled: task.enabled,
        schedule: {
            weekdays: [...(task.schedule.weekdays || [])],
            times: [...(task.schedule.times || [])],
        },
    };
}

function StatusCell({ task }) {
    const chip = STATUS_CHIP[task.lastRunStatus];
    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2">
                {task.lastRunAt ? formatMoment(task.lastRunAt) : 'nog niet gedraaid'}
            </Typography>
            {chip && <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />}
        </Box>
    );
}

/** Het instel-formulier voor de score-taak (venster rond wedstrijden). */
function MatchWindowFields({ draft, setDraft }) {
    const mw = draft.matchWindow;
    return (
        <>
            <Box sx={{ opacity: draft.enabled ? 1 : 0.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Interval</Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={mw.intervalMinutes}
                    disabled={!draft.enabled}
                    onChange={(e, value) => {
                        if (value) setDraft((d) => ({ ...d, matchWindow: { ...d.matchWindow, intervalMinutes: value } }));
                    }}
                >
                    {SYNC_INTERVALS.map((minutes) => (
                        <ToggleButton key={minutes} value={minutes}>{minutes} min</ToggleButton>
                    ))}
                </ToggleButtonGroup>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 3 }}>
                    <FormControl sx={{ minWidth: 260 }} disabled={!draft.enabled}>
                        <InputLabel id="window-label">Ophalen wanneer</InputLabel>
                        <Select
                            labelId="window-label"
                            value={mw.window}
                            label="Ophalen wanneer"
                            onChange={(e) => setDraft((d) => ({ ...d, matchWindow: { ...d.matchWindow, window: e.target.value } }))}
                        >
                            <MenuItem value="matchdays">Rond de speeldagen</MenuItem>
                            <MenuItem value="always">Altijd, ook buiten speeldagen</MenuItem>
                        </Select>
                    </FormControl>
                    {mw.window === 'matchdays' && (
                        <TextField
                            label="Uur na het eindsignaal"
                            type="number"
                            disabled={!draft.enabled}
                            value={mw.hoursAfterFinish}
                            onChange={(e) => setDraft((d) => ({
                                ...d,
                                matchWindow: { ...d.matchWindow, hoursAfterFinish: Math.max(0, Number(e.target.value) || 0) },
                            }))}
                            slotProps={{ htmlInput: { min: 0, max: 24 } }}
                            sx={{ width: 200 }}
                        />
                    )}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                    {mw.window === 'matchdays'
                        ? 'Buiten dat venster staat de sync stil tot de eerstvolgende aftrap. Zo betaal je geen API-aanroepen voor een dag zonder voetbal.'
                        : 'Er wordt op elk interval opgehaald, ook op dagen zonder wedstrijden. Dat kost API-aanroepen zonder dat er iets verandert.'}
                </Typography>
            </Box>
        </>
    );
}

/** Het instel-formulier voor een tijd-gebaseerde taak (weekdagen + tijden). */
function ScheduleFields({ draft, setDraft }) {
    const { weekdays, times } = draft.schedule;

    const setTime = (index, value) => setDraft((d) => {
        const next = [...d.schedule.times];
        next[index] = value;
        return { ...d, schedule: { ...d.schedule, times: next } };
    });
    const addTime = () => setDraft((d) => ({ ...d, schedule: { ...d.schedule, times: [...d.schedule.times, '12:00'] } }));
    const removeTime = (index) => setDraft((d) => ({
        ...d,
        schedule: { ...d.schedule, times: d.schedule.times.filter((_, i) => i !== index) },
    }));

    return (
        <Box sx={{ opacity: draft.enabled ? 1 : 0.5 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Dagen</Typography>
            <ToggleButtonGroup
                size="small"
                value={weekdays}
                disabled={!draft.enabled}
                onChange={(e, value) => setDraft((d) => ({ ...d, schedule: { ...d.schedule, weekdays: value } }))}
            >
                {WEEKDAY_ORDER.map((day) => (
                    <ToggleButton key={day} value={day}>{WEEKDAY_SHORT[day]}</ToggleButton>
                ))}
            </ToggleButtonGroup>

            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>Tijden</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {times.map((time, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <TextField
                            type="time"
                            size="small"
                            disabled={!draft.enabled}
                            value={time}
                            onChange={(e) => setTime(index, e.target.value)}
                            sx={{ width: 160 }}
                        />
                        <IconButton
                            aria-label="Tijd verwijderen"
                            disabled={!draft.enabled || times.length <= 1}
                            onClick={() => removeTime(index)}
                        >
                            <DeleteOutlineIcon />
                        </IconButton>
                    </Box>
                ))}
            </Box>
            <Button startIcon={<AddIcon />} onClick={addTime} disabled={!draft.enabled} sx={{ mt: 1 }}>
                Tijd toevoegen
            </Button>
        </Box>
    );
}

/**
 * Overzicht van de automatische taken van een seizoen: aan/uit, schema, laatste run
 * en handmatig uitvoeren. De taken draaien server-side via de planner-Cloud-Function.
 */
function AdminAutomatischeTaken() {
    const { seasonId, seasonName, isLocked } = useSeason();
    const notify = useNotification();

    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const [dialogTaskId, setDialogTaskId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [runs, setRuns] = useState([]);
    const [runsLoading, setRunsLoading] = useState(false);

    const load = useCallback(async () => {
        const list = await fetchTasks(seasonId);
        setTasks(list);
        return list;
    }, [seasonId]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        load()
            .catch((err) => {
                console.error('Kon de taken niet ophalen:', err);
                if (!cancelled) setError('De automatische taken konden niet opgehaald worden.');
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [load]);

    const refreshRuns = useCallback(async (taskId) => {
        setRunsLoading(true);
        try {
            setRuns(await fetchTaskRuns(seasonId, taskId));
        } catch (err) {
            console.error('Kon de run-log niet ophalen:', err);
            setRuns([]);
        } finally {
            setRunsLoading(false);
        }
    }, [seasonId]);

    const toggleEnabled = async (task) => {
        setBusyId(task.id);
        try {
            await updateTaskConfig(seasonId, task.id, { ...taskConfig(task), enabled: !task.enabled });
            notify.success(!task.enabled ? `${task.label} staat aan.` : `${task.label} staat uit.`);
            await load();
        } catch (err) {
            console.error('Kon de taak niet wijzigen:', err);
            notify.error(`De taak kon niet gewijzigd worden: ${err.message}`);
        } finally {
            setBusyId(null);
        }
    };

    const runNow = async (task) => {
        setBusyId(task.id);
        try {
            const result = await runTaskNow(seasonId, task.id);
            const message = result?.message || 'Taak uitgevoerd.';
            if (result?.status === 'error') notify.error(message);
            else notify.success(message);
            await load();
            if (expandedId === task.id) await refreshRuns(task.id);
        } catch (err) {
            console.error('Kon de taak niet uitvoeren:', err);
            notify.error(`De taak kon niet uitgevoerd worden: ${err.message}`);
        } finally {
            setBusyId(null);
        }
    };

    const openDialog = (task) => {
        setDraft(taskConfig(task));
        setDialogTaskId(task.id);
    };

    const closeDialog = () => {
        setDialogTaskId(null);
        setDraft(null);
    };

    const dialogTask = tasks.find((t) => t.id === dialogTaskId) || null;
    const scheduleInvalid = dialogTask?.triggerType === 'schedule' && draft
        && (draft.schedule.weekdays.length === 0 || draft.schedule.times.length === 0);

    const saveDialog = async () => {
        setSaving(true);
        try {
            await updateTaskConfig(seasonId, dialogTaskId, draft);
            notify.success(`${dialogTask.label} bijgewerkt.`);
            closeDialog();
            await load();
        } catch (err) {
            console.error('Kon de taak niet opslaan:', err);
            notify.error(`De taak kon niet opgeslagen worden: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const toggleExpand = (task) => {
        if (expandedId === task.id) {
            setExpandedId(null);
            return;
        }
        setExpandedId(task.id);
        setRuns([]);
        refreshRuns(task.id);
    };

    return (
        <Box>
            <SeasonBreadcrumb page="Automatische taken" />
            <Typography variant="h4">Automatische taken</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                Terugkerende taken voor {seasonName}. Zet ze aan of uit, bepaal wanneer ze draaien,
                voer ze met de hand uit en zie of de laatste run slaagde.
            </Typography>

            {isLocked && (
                <Alert severity="info" sx={{ mb: 3 }}>
                    Dit seizoen is afgerond. Taken worden voor een afgerond seizoen overgeslagen.
                </Alert>
            )}

            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Taak</TableCell>
                                <TableCell>Actief</TableCell>
                                <TableCell>Schema</TableCell>
                                <TableCell>Laatste run</TableCell>
                                <TableCell align="right">Acties</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tasks.map((task) => (
                                <React.Fragment key={task.id}>
                                    <TableRow hover>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{task.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">{task.description}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Switch
                                                checked={task.enabled}
                                                disabled={busyId === task.id}
                                                onChange={() => toggleEnabled(task)}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{formatSchedule(task)}</Typography>
                                            {task.enabled && task.nextRunAt && (
                                                <Typography variant="caption" color="text.secondary">
                                                    volgende: {formatMoment(task.nextRunAt)}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell><StatusCell task={task} /></TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                                                <Tooltip title="Nu uitvoeren">
                                                    <span>
                                                        <IconButton
                                                            color="primary"
                                                            aria-label="Nu uitvoeren"
                                                            disabled={busyId !== null}
                                                            onClick={() => runNow(task)}
                                                        >
                                                            {busyId === task.id
                                                                ? <CircularProgress size={20} color="inherit" />
                                                                : <PlayArrowIcon />}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Instellen">
                                                    <span>
                                                        <IconButton
                                                            aria-label="Instellen"
                                                            disabled={busyId !== null}
                                                            onClick={() => openDialog(task)}
                                                        >
                                                            <TuneIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                                <Tooltip title="Run-log tonen">
                                                    <IconButton
                                                        aria-label="Run-log tonen"
                                                        onClick={() => toggleExpand(task)}
                                                    >
                                                        {expandedId === task.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell colSpan={5} sx={{ py: 0, border: 0 }}>
                                            <Collapse in={expandedId === task.id} timeout="auto" unmountOnExit>
                                                <Box sx={{ py: 2 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Laatste runs</Typography>
                                                    {runsLoading ? (
                                                        <CircularProgress size={20} />
                                                    ) : runs.length === 0 ? (
                                                        <Typography variant="body2" color="text.secondary">
                                                            Nog geen runs vastgelegd.
                                                        </Typography>
                                                    ) : (
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Wanneer</TableCell>
                                                                    <TableCell>Trigger</TableCell>
                                                                    <TableCell>Status</TableCell>
                                                                    <TableCell>Resultaat</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {runs.map((run) => {
                                                                    const chip = STATUS_CHIP[run.status];
                                                                    return (
                                                                        <TableRow key={run.id}>
                                                                            <TableCell>{formatMoment(run.startedAt)}</TableCell>
                                                                            <TableCell>{run.trigger === 'manual' ? 'Handmatig' : 'Automatisch'}</TableCell>
                                                                            <TableCell>
                                                                                {chip && <Chip size="small" variant="outlined" color={chip.color} label={chip.label} />}
                                                                            </TableCell>
                                                                            <TableCell>{run.summary || run.error || '—'}</TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })}
                                                            </TableBody>
                                                        </Table>
                                                    )}
                                                </Box>
                                            </Collapse>
                                        </TableCell>
                                    </TableRow>
                                </React.Fragment>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogTaskId !== null} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>{dialogTask?.label} instellen</DialogTitle>
                <DialogContent dividers>
                    {dialogTask && draft && (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {dialogTask.description}
                            </Typography>
                            <FormControlLabel
                                control={(
                                    <Switch
                                        checked={draft.enabled}
                                        onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
                                    />
                                )}
                                label="Automatisch uitvoeren"
                                sx={{ mb: 1 }}
                            />
                            {dialogTask.triggerType === 'matchWindow'
                                ? <MatchWindowFields draft={draft} setDraft={setDraft} />
                                : <ScheduleFields draft={draft} setDraft={setDraft} />}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Annuleren</Button>
                    <Button variant="contained" onClick={saveDialog} disabled={saving || scheduleInvalid}>
                        {saving ? 'Bezig…' : 'Opslaan'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default AdminAutomatischeTaken;
